import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, RouterLink } from '@angular/router';
import { AdminDashboardService } from '../../services/admin-dashboard.service';
import { EntityService } from '../../services/entity.service';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ContractModalComponent } from './contract-modal.component';
import { NotificationService } from '../../services/notification.service';
import { StarTarifHistoryPagination, StarTarifHistoryRow } from '../../services/star-tarif.service';

interface Video {
  id: number;
  title: string;
  file_url: string;
  thumbnail: string;
  createdAt: Date;
}

interface CelebrityDetails {
  id: string;
  user_id: string;
  name: string;
  email: string;
  profile_picture: string;
  category: string;
  biography: string;
  status: 'active' | 'inactive' | 'pending';
  official: any;
  registrationDate: Date;
  
  stats: {
    totalVideos: number;
    completionRate: number;
    averageRating: number;
    totalRevenue: number;
    responseTime: number;
  };
  recentActivity: {
    date: Date;
    action: string;
    details: string;
  }[];
}

interface Contract {
  id: string;
  star_id: string;
  cle_repartition: number;
  file_url: string;
  created_at: string;
  updated_at: string;
}

@Component({
  selector: 'app-admin-celebrity-details',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, RouterLink, ContractModalComponent, NgbModule],
  templateUrl: './admin-celebrity-details.component.html',
  styleUrls: ['./admin-celebrity-details.component.scss']
})
export class AdminCelebrityDetailsComponent implements OnInit {
  celebrity: CelebrityDetails;
  contract: Contract | null = null;
  contractModalRef: any = null;
  celebrityId!: number;
  loading: boolean = true;
  error: string = '';
  public videos: Video[] = [];
  selectedVideo: Video | null = null;
  revenues: {
    usd: number | 0;
    cdf: number | 0;
  } = {
    usd: 0,
    cdf: 0
  };

  /** Historique des tarifs personnalisés (star_tarif_histories) */
  tarifHistoryItems: StarTarifHistoryRow[] = [];
  tarifHistoryLoading = false;
  tarifHistoryError: string | null = null;
  tarifHistoryPagination: StarTarifHistoryPagination | null = null;
  tarifHistoryTypeFilter = '';
  readonly tarifHistoryPerPage = 10;

  readonly typeRequestFilterOptions: { key: string; label: string }[] = [
    { key: '', label: 'Tous les types' },
    { key: 'video', label: 'Vidéo personnalisée' },
    { key: 'dedicace', label: 'Dédicace' },
    { key: 'shoutout', label: 'Shoutout' },
    { key: 'promotion', label: 'Promotion' },
    { key: 'message', label: 'Message' },
    { key: 'other', label: 'Autre' },
  ];

  constructor(
    private route: ActivatedRoute,
    private adminService: AdminDashboardService,
    private entityService: EntityService,
    private notificationService: NotificationService,
    private modalService: NgbModal
  ) {
    this.celebrity = {} as CelebrityDetails;
  }

  ngOnInit() {
    this.celebrityId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadCelebrityDetails();
    this.loadContract();
    this.loadTarifHistory(1);
    this.entityService.getDataById('/userVideos', this.celebrityId).subscribe((data: any) => {
      if (data) {
        this.videos = data;
      }
    });
  }

  loadTarifHistory(page = 1): void {
    this.tarifHistoryLoading = true;
    this.tarifHistoryError = null;
    this.adminService
      .getAdminStarTarifHistory(this.celebrityId, {
        page,
        per_page: this.tarifHistoryPerPage,
        type_request: this.tarifHistoryTypeFilter || undefined,
      })
      .subscribe({
        next: ({ items, pagination }) => {
          this.tarifHistoryItems = items;
          this.tarifHistoryPagination = pagination;
          this.tarifHistoryLoading = false;
        },
        error: (err) => {
          this.tarifHistoryError =
            err?.error?.message || "Impossible de charger l'historique des tarifs.";
          this.tarifHistoryLoading = false;
        },
      });
  }

  onTarifHistoryFilterChange(): void {
    this.loadTarifHistory(1);
  }

  goTarifHistoryPage(delta: number): void {
    const p = this.tarifHistoryPagination;
    if (!p) return;
    const next = p.current_page + delta;
    if (next < 1 || next > p.last_page) return;
    this.loadTarifHistory(next);
  }

  getTarifTypeLabel(typeRequest: string): string {
    const labels: Record<string, string> = {
      video: 'Vidéo personnalisée',
      dedicace: 'Dédicace',
      shoutout: 'Shoutout',
      promotion: 'Promotion',
      message: 'Message',
      other: 'Autre',
    };
    return labels[typeRequest] ?? typeRequest;
  }

  getTarifActionLabel(action: string): string {
    const map: Record<string, string> = {
      created: 'Création',
      updated: 'Modification',
      deleted: 'Suppression',
    };
    return map[action] ?? action;
  }

  formatTarifAmount(amount: string | null, currency: string | null): string {
    if (amount == null || amount === '') {
      return '—';
    }
    const c = currency?.trim() ? ` ${currency}` : '';
    return `${amount}${c}`;
  }

  private loadCelebrityDetails() {
    this.loading = true;
    this.adminService.getCelebrityDetails(this.celebrityId).subscribe({
      next: (data) => {
        console.log(data)
        this.celebrity = data.data;
        this.revenues.usd = data.revenues.usd;
        this.revenues.cdf = data.revenues.cdf;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Erreur lors du chargement des détails';
        this.loading = false;
      }
    });
  }

  loadContract() {
    this.entityService.getDataById2(`/contrats`, this.celebrityId).subscribe({
      next: (response: any) => {
        console.log('Contract loaded:', response);
        this.contract = response;
        
      },
      error: (error) => {
        if (error.status === 404) {
          console.info('Aucun contrat trouvé pour cette célébrité.');
          this.contract = null; // ou undefined selon ton usage
        } else {
          console.error('Erreur lors du chargement du contrat:', error);
        }
      }
    });
  }
  

  updateStatus(status: 'active' | 'inactive') {
    this.adminService.updateCelebrityStatus(this.celebrityId, status).subscribe({
      next: () => this.loadCelebrityDetails(),
      error: (err) => this.error = 'Erreur lors de la mise à jour du statut'
    });
  }

  updateOfficialize(official: 1 | 0) {
    this.adminService.officializeCelebrityAccount(this.celebrityId, official).subscribe({
      next: () => this.loadCelebrityDetails(),
      error: (err) => this.error = 'Erreur lors de la mise à jour du statut'
    });
  }

  openVideoModal(video: Video) {
    this.selectedVideo = video;
  }

  closeVideoModal() {
    this.selectedVideo = null;
  }

  openContractModal(isEdit = false) {
    const modalRef = this.modalService.open(ContractModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.starId = this.celebrityId;
    modalRef.componentInstance.isEdit = isEdit;
    modalRef.componentInstance.userId = this.celebrityId;
    if (isEdit && this.contract) {
      modalRef.componentInstance.contract = this.contract;
    }

    modalRef.componentInstance.contractUpdated.subscribe(() => {
      this.loadContract();
    });
  }

  openContractPdf() {
    if (this.contract?.file_url) {
      window.open(this.contract.file_url, '_blank');
    }
  }
}